import type {
  CaptainSave, Sighting, Vec2, WreckMemorial, HurricaneSnapshot, FogBankSnapshot,
  ChartDiscovery, ChartContact,
} from '@realseas/shared';
import { FOG_CELL_SIZE_KM, ISLAND_LANDING_RANGE_KM, getPort, LANDMASSES, pointInPolygon } from '@realseas/shared';

const NAKED_EYE_KM = 12;
const TELESCOPE_KM = 35;
/** Default map zoom: world radius (km) shown around the player. Pan/zoom override this. */
const DEFAULT_RADIUS_KM = 200;
const MIN_RADIUS_KM = 30;
/** Cádiz → Tortuga is ~7000 km, so 8000 km radius lets the chart fit a full Atlantic crossing. */
const MAX_RADIUS_KM = 8000;
const ZOOM_FACTOR = 1.25;
/** How far past the explored bounding box you can pan, in km. */
const PAN_BUFFER_KM = 80;
/** Padding around the bounding box when "Fit Explored" is clicked. */
const FIT_PADDING_KM = 60;

function ownedOf(captain: CaptainSave, itemId: string): number {
  return captain.inventory.find(i => i.itemId === itemId)?.quantity ?? 0;
}

interface MapState {
  canvas: HTMLCanvasElement;
  modal: HTMLElement;
  keyHandler: (e: KeyboardEvent) => void;
  // Latest data, used by event handlers that need to redraw
  captain: CaptainSave;
  fog: Set<string>;
  memorials: WreckMemorial[];
  hurricanes: HurricaneSnapshot[];
  fogBanks: FogBankSnapshot[];
  // Viewport — null centerX/Y means "follow the player"
  centerX: number | null;
  centerY: number | null;
  radiusKm: number;
  // One-finger / mouse drag
  dragging: boolean;
  dragStartClientX: number;
  dragStartClientY: number;
  dragStartCenterX: number;
  dragStartCenterY: number;
  // Two-finger pinch (mobile)
  pinching: boolean;
  pinchStartDistance: number;
  pinchStartRadiusKm: number;
  pinchStartMidClientX: number;
  pinchStartMidClientY: number;
  pinchStartCenterX: number;
  pinchStartCenterY: number;
  // Cached body overflow so we can restore on close
  prevBodyOverflow: string;
}

let state: MapState | null = null;

export function isMapOpen(): boolean {
  return state !== null;
}

export function openMapModal(
  captain: CaptainSave,
  fog: Set<string>,
  memorials: WreckMemorial[] = [],
  hurricanes: HurricaneSnapshot[] = [],
  fogBanks: FogBankSnapshot[] = [],
): void {
  closeMapModal();

  const modal = document.createElement('div');
  modal.id = 'map-modal';
  modal.className = 'map-modal';
  const portName = captain.portId ? getPort(captain.portId)?.name : null;
  modal.innerHTML = `
    <div class="map-modal-backdrop"></div>
    <div class="map-modal-panel">
      <div class="map-header">
        <h2>Captain's Chart <span class="map-lede">— click and drag to pan, scroll to zoom</span></h2>
        <div class="map-header-actions">
          <button class="secondary" id="map-recenter-btn">Ship</button>
          <button class="secondary" id="map-fit-btn">Fit Explored</button>
          ${portName ? `<button class="secondary" id="map-origin-btn">${portName}</button>` : ''}
          <button class="secondary" id="map-close-btn">Close</button>
        </div>
      </div>
      <canvas id="map-canvas" width="640" height="640"></canvas>
      <div class="map-legend">
        <span class="legend-item"><span class="dot dot-self"></span> You</span>
        <span class="legend-item"><span class="dot dot-merchant"></span> Merchant</span>
        <span class="legend-item"><span class="dot dot-peace"></span> Captain (peace)</span>
        <span class="legend-item"><span class="dot dot-war"></span> Captain (war)</span>
        <span class="legend-item"><span class="dot dot-whale"></span> Whale</span>
        <span class="legend-item"><span class="dot dot-island"></span> Island</span>
        <span class="legend-item"><span class="dot dot-charted"></span> Charted island</span>
        <span class="legend-item"><span class="dot dot-wreck"></span> Wreck site</span>
        <span class="legend-item"><span class="dot dot-lastseen"></span> Last seen</span>
        <span class="legend-item"><span class="dot dot-tortuga"></span> Destination bearing</span>
        <span class="legend-item"><span class="dot dot-memorial"></span> Lost captain</span>
      </div>
    </div>
  `;
  document.body.appendChild(modal);

  // Lock body scroll while the chart is open
  const prevBodyOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';

  const canvas = modal.querySelector<HTMLCanvasElement>('#map-canvas')!;

  const keyHandler = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') closeMapModal();
  };
  document.addEventListener('keydown', keyHandler);

  state = {
    canvas, modal, keyHandler, captain, fog, memorials, hurricanes, fogBanks,
    centerX: null, centerY: null, radiusKm: DEFAULT_RADIUS_KM,
    dragging: false, dragStartClientX: 0, dragStartClientY: 0,
    dragStartCenterX: 0, dragStartCenterY: 0,
    pinching: false, pinchStartDistance: 0, pinchStartRadiusKm: 0,
    pinchStartMidClientX: 0, pinchStartMidClientY: 0,
    pinchStartCenterX: 0, pinchStartCenterY: 0,
    prevBodyOverflow,
  };

  attachInteractionHandlers(canvas);
  drawMap();

  modal.querySelector<HTMLButtonElement>('#map-close-btn')!.onclick = closeMapModal;
  modal.querySelector<HTMLButtonElement>('#map-recenter-btn')!.onclick = recenterOnShip;
  modal.querySelector<HTMLButtonElement>('#map-fit-btn')!.onclick = fitExplored;
  modal.querySelector<HTMLButtonElement>('#map-origin-btn')?.addEventListener('click', jumpToOrigin);
  modal.querySelector<HTMLElement>('.map-modal-backdrop')!.onclick = closeMapModal;
}

export function closeMapModal(): void {
  if (!state) return;
  document.removeEventListener('keydown', state.keyHandler);
  document.body.style.overflow = state.prevBodyOverflow;
  state.modal.remove();
  state = null;
}

export function refreshMapIfOpen(
  captain: CaptainSave,
  fog: Set<string>,
  memorials: WreckMemorial[] = [],
  hurricanes: HurricaneSnapshot[] = [],
  fogBanks: FogBankSnapshot[] = [],
): void {
  if (!state) return;
  state.captain = captain;
  state.fog = fog;
  state.memorials = memorials;
  state.hurricanes = hurricanes;
  state.fogBanks = fogBanks;
  drawMap();
}

function recenterOnShip(): void {
  if (!state) return;
  state.centerX = null;
  state.centerY = null;
  state.radiusKm = DEFAULT_RADIUS_KM;
  drawMap();
}

/** Fit the entire explored bounding box (plus the player) into view. */
function fitExplored(): void {
  if (!state) return;
  const bbox = exploredBbox();
  if (!bbox) {
    recenterOnShip();
    return;
  }
  state.centerX = (bbox.minX + bbox.maxX) / 2;
  state.centerY = (bbox.minY + bbox.maxY) / 2;
  // Use the larger half-extent so both axes fit; we render to a square canvas
  const halfExtent = Math.max(
    (bbox.maxX - bbox.minX) / 2,
    (bbox.maxY - bbox.minY) / 2,
  ) + FIT_PADDING_KM;
  state.radiusKm = Math.max(MIN_RADIUS_KM, Math.min(MAX_RADIUS_KM, halfExtent));
  drawMap();
}

/** Jump the view to the captain's port of embarkation at default zoom. */
function jumpToOrigin(): void {
  if (!state) return;
  const portId = state.captain.portId;
  if (!portId) return;
  const port = getPort(portId);
  if (!port) return;
  state.centerX = port.position.x;
  state.centerY = port.position.y;
  state.radiusKm = DEFAULT_RADIUS_KM;
  drawMap();
}

/** Bounding box of all discovered cells, in world coords. Includes the player position. */
function exploredBbox(): { minX: number; maxX: number; minY: number; maxY: number } | null {
  if (!state) return null;
  const ship = state.captain.ship;
  if (state.fog.size === 0) {
    if (!ship) return null;
    return { minX: ship.position.x, maxX: ship.position.x, minY: ship.position.y, maxY: ship.position.y };
  }
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const k of state.fog) {
    const idx = k.indexOf('|');
    const cellX = Number(k.slice(0, idx));
    const cellY = Number(k.slice(idx + 1));
    const wx = (cellX + 0.5) * FOG_CELL_SIZE_KM;
    const wy = (cellY + 0.5) * FOG_CELL_SIZE_KM;
    if (wx < minX) minX = wx;
    if (wx > maxX) maxX = wx;
    if (wy < minY) minY = wy;
    if (wy > maxY) maxY = wy;
  }
  if (ship) {
    minX = Math.min(minX, ship.position.x);
    maxX = Math.max(maxX, ship.position.x);
    minY = Math.min(minY, ship.position.y);
    maxY = Math.max(maxY, ship.position.y);
  }
  return { minX, maxX, minY, maxY };
}

// ── Pan / zoom interaction ──────────────────────────────────────────────────

function attachInteractionHandlers(canvas: HTMLCanvasElement): void {
  canvas.addEventListener('mousedown', onMouseDown);
  canvas.addEventListener('mousemove', onMouseMove);
  // window listeners so a drag that wanders off the canvas still ends cleanly
  window.addEventListener('mouseup', onMouseUp);
  window.addEventListener('mousemove', onWindowMouseMove);
  canvas.addEventListener('wheel', onWheel, { passive: false });
  // Touch (mobile): single-finger pan, two-finger pinch zoom
  canvas.addEventListener('touchstart', onTouchStart, { passive: false });
  canvas.addEventListener('touchmove', onTouchMove, { passive: false });
  canvas.addEventListener('touchend', onTouchEnd, { passive: false });
  canvas.addEventListener('touchcancel', onTouchEnd, { passive: false });
}

function viewCenter(): { x: number; y: number } {
  if (!state) return { x: 0, y: 0 };
  if (state.centerX !== null && state.centerY !== null) {
    return { x: state.centerX, y: state.centerY };
  }
  const ship = state.captain.ship;
  return { x: ship?.position.x ?? 0, y: ship?.position.y ?? 0 };
}

function onMouseDown(e: MouseEvent): void {
  if (!state) return;
  e.preventDefault();
  state.dragging = true;
  state.dragStartClientX = e.clientX;
  state.dragStartClientY = e.clientY;
  const c = viewCenter();
  state.dragStartCenterX = c.x;
  state.dragStartCenterY = c.y;
  state.canvas.style.cursor = 'grabbing';
}

function onMouseMove(e: MouseEvent): void {
  if (!state) return;
  // Hover cursor when not dragging
  if (!state.dragging) state.canvas.style.cursor = 'grab';
}

function onWindowMouseMove(e: MouseEvent): void {
  if (!state || !state.dragging) return;
  const pxPerKm = pixelsPerKm();
  const dxKm = (e.clientX - state.dragStartClientX) / pxPerKm;
  const dyKm = (e.clientY - state.dragStartClientY) / pxPerKm;
  const next = clampToExplored(
    state.dragStartCenterX - dxKm,
    state.dragStartCenterY - dyKm,
  );
  state.centerX = next.x;
  state.centerY = next.y;
  drawMap();
}

function onMouseUp(): void {
  if (!state || !state.dragging) return;
  state.dragging = false;
  state.canvas.style.cursor = 'grab';
}

function onWheel(e: WheelEvent): void {
  if (!state) return;
  e.preventDefault();
  // Zoom around the cursor position so the point under the mouse stays put
  const rect = state.canvas.getBoundingClientRect();
  const mxPx = (e.clientX - rect.left) * (state.canvas.width / rect.width);
  const myPx = (e.clientY - rect.top) * (state.canvas.height / rect.height);
  const before = pixelToWorld(mxPx, myPx);

  const factor = e.deltaY < 0 ? 1 / ZOOM_FACTOR : ZOOM_FACTOR;
  state.radiusKm = Math.max(MIN_RADIUS_KM, Math.min(MAX_RADIUS_KM, state.radiusKm * factor));

  const after = pixelToWorld(mxPx, myPx);
  // Detach from "follow player" the first time the user zooms-and-drags-implicitly
  const c = viewCenter();
  const next = clampToExplored(
    c.x + (before.x - after.x),
    c.y + (before.y - after.y),
  );
  state.centerX = next.x;
  state.centerY = next.y;
  drawMap();
}

function touchDistance(a: Touch, b: Touch): number {
  return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
}

function touchMidpoint(a: Touch, b: Touch): { x: number; y: number } {
  return { x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 };
}

function onTouchStart(e: TouchEvent): void {
  if (!state) return;
  e.preventDefault();
  if (e.touches.length === 1) {
    // Single-finger drag — same model as mousedown
    const t = e.touches[0];
    state.dragging = true;
    state.pinching = false;
    state.dragStartClientX = t.clientX;
    state.dragStartClientY = t.clientY;
    const c = viewCenter();
    state.dragStartCenterX = c.x;
    state.dragStartCenterY = c.y;
  } else if (e.touches.length >= 2) {
    // Two-finger pinch begins; cancel any drag
    state.dragging = false;
    state.pinching = true;
    state.pinchStartDistance = touchDistance(e.touches[0], e.touches[1]);
    state.pinchStartRadiusKm = state.radiusKm;
    const m = touchMidpoint(e.touches[0], e.touches[1]);
    state.pinchStartMidClientX = m.x;
    state.pinchStartMidClientY = m.y;
    const c = viewCenter();
    state.pinchStartCenterX = c.x;
    state.pinchStartCenterY = c.y;
  }
}

function onTouchMove(e: TouchEvent): void {
  if (!state) return;
  e.preventDefault();
  if (state.pinching && e.touches.length >= 2) {
    const newDist = touchDistance(e.touches[0], e.touches[1]);
    if (newDist <= 0) return;
    // Pinch out (newDist > startDist) → zoom in (radius shrinks)
    // Pinch in  (newDist < startDist) → zoom out (radius grows)
    const fingerRatio = newDist / state.pinchStartDistance;
    state.radiusKm = Math.max(MIN_RADIUS_KM, Math.min(MAX_RADIUS_KM, state.pinchStartRadiusKm / fingerRatio));
    // Pan along with midpoint movement
    const m = touchMidpoint(e.touches[0], e.touches[1]);
    const pxPerKm = pixelsPerKm();
    const dxKm = (m.x - state.pinchStartMidClientX) / pxPerKm;
    const dyKm = (m.y - state.pinchStartMidClientY) / pxPerKm;
    const next = clampToExplored(
      state.pinchStartCenterX - dxKm,
      state.pinchStartCenterY - dyKm,
    );
    state.centerX = next.x;
    state.centerY = next.y;
    drawMap();
  } else if (state.dragging && e.touches.length === 1) {
    const t = e.touches[0];
    const pxPerKm = pixelsPerKm();
    const dxKm = (t.clientX - state.dragStartClientX) / pxPerKm;
    const dyKm = (t.clientY - state.dragStartClientY) / pxPerKm;
    const next = clampToExplored(
      state.dragStartCenterX - dxKm,
      state.dragStartCenterY - dyKm,
    );
    state.centerX = next.x;
    state.centerY = next.y;
    drawMap();
  }
}

function onTouchEnd(e: TouchEvent): void {
  if (!state) return;
  if (e.touches.length === 0) {
    state.dragging = false;
    state.pinching = false;
  } else if (e.touches.length === 1 && state.pinching) {
    // Pinch released down to one finger — switch to drag mode without a jump
    state.pinching = false;
    const t = e.touches[0];
    state.dragging = true;
    state.dragStartClientX = t.clientX;
    state.dragStartClientY = t.clientY;
    const c = viewCenter();
    state.dragStartCenterX = c.x;
    state.dragStartCenterY = c.y;
  }
}

function pixelsPerKm(): number {
  if (!state) return 1;
  const w = state.canvas.width;
  const h = state.canvas.height;
  return (Math.min(w, h) / 2) / state.radiusKm;
}

function pixelToWorld(px: number, py: number): { x: number; y: number } {
  if (!state) return { x: 0, y: 0 };
  const w = state.canvas.width;
  const h = state.canvas.height;
  const c = viewCenter();
  const pxPerKm = pixelsPerKm();
  return {
    x: c.x + (px - w / 2) / pxPerKm,
    y: c.y + (py - h / 2) / pxPerKm,
  };
}

/**
 * Clamp the proposed map center so it stays within the explored bounding
 * box (plus a buffer). Falls back to the player's position if the captain
 * has no fog yet.
 */
function clampToExplored(x: number, y: number): { x: number; y: number } {
  const bbox = exploredBbox();
  if (!bbox) return { x, y };
  const lo = { x: bbox.minX - PAN_BUFFER_KM, y: bbox.minY - PAN_BUFFER_KM };
  const hi = { x: bbox.maxX + PAN_BUFFER_KM, y: bbox.maxY + PAN_BUFFER_KM };
  return {
    x: Math.max(lo.x, Math.min(hi.x, x)),
    y: Math.max(lo.y, Math.min(hi.y, y)),
  };
}

// ── Render ──────────────────────────────────────────────────────────────────

function drawMap(): void {
  if (!state) return;
  const { canvas, captain, fog, radiusKm } = state;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const w = canvas.width;
  const h = canvas.height;
  const cx = w / 2;
  const cy = h / 2;

  const hasTelescope = ownedOf(captain, 'telescope') > 0;
  const hasCompass = ownedOf(captain, 'compass') > 0;
  const visualRange = hasTelescope ? TELESCOPE_KM : NAKED_EYE_KM;
  const pxPerKm = (Math.min(w, h) / 2) / radiusKm;

  const center = viewCenter();
  const playerX = captain.ship?.position.x ?? center.x;
  const playerY = captain.ship?.position.y ?? center.y;

  // Pitch-black unexplored sea
  ctx.fillStyle = '#070d14';
  ctx.fillRect(0, 0, w, h);

  // Discovered fog cells — iterate the fog set, draw any cell touching the viewport.
  // Each cell is colored green or blue depending on whether its center is on land,
  // so coastlines reveal themselves only as the captain sails along them.
  const cellPx = FOG_CELL_SIZE_KM * pxPerKm;
  const SEA_COLOR = '#1a3548';
  const LAND_COLOR = '#3a5a32';
  for (const k of fog) {
    const idx = k.indexOf('|');
    const cellX = Number(k.slice(0, idx));
    const cellY = Number(k.slice(idx + 1));
    const wx = cellX * FOG_CELL_SIZE_KM;
    const wy = cellY * FOG_CELL_SIZE_KM;
    const px = cx + (wx - center.x) * pxPerKm;
    const py = cy + (wy - center.y) * pxPerKm;
    if (px + cellPx < 0 || px > w || py + cellPx < 0 || py > h) continue;
    const cellCenter = { x: wx + FOG_CELL_SIZE_KM * 0.5, y: wy + FOG_CELL_SIZE_KM * 0.5 };
    const onLand = LANDMASSES.some(m => pointInPolygon(cellCenter, m.polygon));
    ctx.fillStyle = onLand ? LAND_COLOR : SEA_COLOR;
    ctx.fillRect(px, py, cellPx + 1, cellPx + 1);
  }

  // Inner lit area — your CURRENT visual range, drawn at the SHIP's position.
  // (Important: visibility is around the ship, not the map view center.)
  const shipScreenX = cx + (playerX - center.x) * pxPerKm;
  const shipScreenY = cy + (playerY - center.y) * pxPerKm;
  const litR = visualRange * pxPerKm;
  const lit = ctx.createRadialGradient(shipScreenX, shipScreenY, litR * 0.4, shipScreenX, shipScreenY, litR);
  lit.addColorStop(0, 'rgba(80, 110, 134, 0.55)');
  lit.addColorStop(0.85, 'rgba(50, 78, 100, 0.4)');
  lit.addColorStop(1, 'rgba(20, 40, 56, 0)');
  ctx.fillStyle = lit;
  ctx.beginPath();
  ctx.arc(shipScreenX, shipScreenY, litR, 0, 2 * Math.PI);
  ctx.fill();

  // Major range rings around the SHIP every 50 km, only at moderate zooms
  if (radiusKm <= 400) {
    ctx.strokeStyle = 'rgba(255, 240, 200, 0.06)';
    ctx.lineWidth = 1;
    for (let r = 50; r < radiusKm * 1.5; r += 50) {
      ctx.beginPath();
      ctx.arc(shipScreenX, shipScreenY, r * pxPerKm, 0, 2 * Math.PI);
      ctx.stroke();
    }
  }
  // Visual-range ring (your edge of sight RIGHT NOW)
  ctx.strokeStyle = 'rgba(255, 240, 200, 0.35)';
  ctx.setLineDash([5, 5]);
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.arc(shipScreenX, shipScreenY, litR, 0, 2 * Math.PI);
  ctx.stroke();
  ctx.setLineDash([]);

  // Cardinal markers (always against the canvas edge)
  ctx.fillStyle = '#f7eed8';
  ctx.font = 'bold 13px Georgia, serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const cardR = (Math.min(w, h) / 2) - 14;
  ctx.fillText('N', cx, cy - cardR);
  ctx.fillText('S', cx, cy + cardR);
  ctx.fillText('E', cx + cardR, cy);
  ctx.fillText('W', cx - cardR, cy);

  // Destination bearing — gold tick on the rim, drawn from the SHIP not view center
  const destination = captain.destinationId ? getPort(captain.destinationId) : undefined;
  if (captain.destinationBearing !== null) {
    const tr = cardR - 4;
    const tx = shipScreenX + Math.cos(captain.destinationBearing) * tr;
    const ty = shipScreenY + Math.sin(captain.destinationBearing) * tr;
    if (tx > 6 && tx < w - 6 && ty > 6 && ty < h - 6) {
      ctx.fillStyle = '#d9a72a';
      ctx.beginPath();
      ctx.arc(tx, ty, 6, 0, 2 * Math.PI);
      ctx.fill();
      ctx.strokeStyle = '#3a2a18';
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.fillStyle = '#3a2a18';
      ctx.font = 'bold 9px Georgia, serif';
      ctx.fillText((destination?.name ?? 'T').charAt(0).toUpperCase(), tx, ty);
    }
  }

  // Destination port — drawn as a city marker once the captain has charted
  // anything near it (or always for the port they departed from, below).
  if (destination && destination.id !== captain.portId) {
    const dpx = cx + (destination.cityPosition.x - center.x) * pxPerKm;
    const dpy = cy + (destination.cityPosition.y - center.y) * pxPerKm;
    const cellX = Math.floor(destination.cityPosition.x / FOG_CELL_SIZE_KM);
    const cellY = Math.floor(destination.cityPosition.y / FOG_CELL_SIZE_KM);
    if (fog.has(`${cellX}|${cellY}`) && dpx > -16 && dpx < w + 16 && dpy > -16 && dpy < h + 16) {
      drawCityMarker(ctx, dpx, dpy, destination.name);
    }
  }

  // City of origin — drawn on the coast, with a small castle-tower icon
  if (captain.portId) {
    const port = getPort(captain.portId);
    if (port) {
      const ppx = cx + (port.cityPosition.x - center.x) * pxPerKm;
      const ppy = cy + (port.cityPosition.y - center.y) * pxPerKm;
      if (ppx > -16 && ppx < w + 16 && ppy > -16 && ppy < h + 16) {
        drawCityMarker(ctx, ppx, ppy, port.name);
      }
    }
  }

  // Embark point — where the voyage actually began (offshore from the city)
  if (captain.embarkPosition) {
    const epx = cx + (captain.embarkPosition.x - center.x) * pxPerKm;
    const epy = cy + (captain.embarkPosition.y - center.y) * pxPerKm;
    if (epx > -16 && epx < w + 16 && epy > -16 && epy < h + 16) {
      drawEmbarkMarker(ctx, epx, epy);
    }
  }

  // Memorials — permanent landmarks for every captain who was lost.
  // Only revealed where the player's fog has touched their cell.
  for (const m of state.memorials) {
    const cellX = Math.floor(m.x / FOG_CELL_SIZE_KM);
    const cellY = Math.floor(m.y / FOG_CELL_SIZE_KM);
    if (!fog.has(`${cellX}|${cellY}`)) continue;
    const mx = cx + (m.x - center.x) * pxPerKm;
    const my = cy + (m.y - center.y) * pxPerKm;
    if (mx < -16 || mx > w + 16 || my < -16 || my > h + 16) continue;
    drawMemorialMarker(ctx, mx, my, m, radiusKm);
  }

  // Fog banks — gray patches on the chart. Drawn underneath sightings + ship so
  // sightings still pop. Only visible where the player has been near (fogged-out
  // areas of the map don't reveal weather they haven't seen).
  for (const fb of state.fogBanks) {
    const cellX = Math.floor(fb.x / FOG_CELL_SIZE_KM);
    const cellY = Math.floor(fb.y / FOG_CELL_SIZE_KM);
    if (!fog.has(`${cellX}|${cellY}`)) continue;
    const fbx = cx + (fb.x - center.x) * pxPerKm;
    const fby = cy + (fb.y - center.y) * pxPerKm;
    const fbR = fb.radiusKm * pxPerKm;
    if (fbx + fbR < 0 || fbx - fbR > w || fby + fbR < 0 || fby - fbR > h) continue;
    const fgrad = ctx.createRadialGradient(fbx, fby, fbR * 0.2, fbx, fby, fbR);
    fgrad.addColorStop(0, 'rgba(190, 200, 210, 0.55)');
    fgrad.addColorStop(1, 'rgba(190, 200, 210, 0)');
    ctx.fillStyle = fgrad;
    ctx.beginPath();
    ctx.arc(fbx, fby, fbR, 0, 2 * Math.PI);
    ctx.fill();
  }

  // Hurricanes — visible to all captains. Drawn as a swirling spiral with a
  // dashed radius ring. True hurricanes are red-tinted; tropical storms grey.
  for (const hc of state.hurricanes) {
    const hx = cx + (hc.x - center.x) * pxPerKm;
    const hy = cy + (hc.y - center.y) * pxPerKm;
    const hR = hc.radiusKm * pxPerKm;
    if (hx + hR < 0 || hx - hR > w || hy + hR < 0 || hy - hR > h) continue;
    drawHurricane(ctx, hx, hy, hR, hc.isTrueHurricane);
  }

  // Chart memory — charted islands and wreck sites. Islands currently in sight
  // are drawn live below (with their landing ring), so skip those here.
  const liveIslandIds = new Set(
    captain.sightings.filter(s => s.entityKind === 'island' && s.entityId !== null).map(s => s.entityId!),
  );
  for (const d of captain.discoveries ?? []) {
    if (d.kind === 'island' && liveIslandIds.has(d.refId)) continue;
    const dx = cx + (d.x - center.x) * pxPerKm;
    const dy = cy + (d.y - center.y) * pxPerKm;
    if (dx < -20 || dx > w + 20 || dy < -20 || dy > h + 20) continue;
    drawDiscovery(ctx, dx, dy, d, radiusKm);
  }

  // Last-seen markers — one per ship / whale / shoal that has slipped out of sight.
  for (const c of captain.lastSeen ?? []) {
    const lx = cx + (c.x - center.x) * pxPerKm;
    const ly = cy + (c.y - center.y) * pxPerKm;
    if (lx < -20 || lx > w + 20 || ly < -20 || ly > h + 20) continue;
    drawLastSeen(ctx, lx, ly, c, radiusKm, Date.now());
  }

  // Sightings — placed at world coords (ship + bearing*distance), then mapped to screen
  for (const s of captain.sightings) {
    const swx = playerX + Math.cos(s.bearing) * s.distanceKm;
    const swy = playerY + Math.sin(s.bearing) * s.distanceKm;
    const ex = cx + (swx - center.x) * pxPerKm;
    const ey = cy + (swy - center.y) * pxPerKm;
    if (ex < -20 || ex > w + 20 || ey < -20 || ey > h + 20) continue;
    drawSighting(ctx, ex, ey, s, { x: swx, y: swy }, pxPerKm);
  }

  // Player ship at its world position, rotated to heading if compass aboard
  const heading = captain.ship?.heading ?? 0;
  ctx.save();
  ctx.translate(shipScreenX, shipScreenY);
  if (hasCompass) ctx.rotate(heading);
  ctx.fillStyle = '#f7eed8';
  ctx.strokeStyle = '#3a2a18';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(11, 0);
  ctx.lineTo(-7, -7);
  ctx.lineTo(-4, 0);
  ctx.lineTo(-7, 7);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.restore();

  // Wind indicator (top-left)
  if (captain.wind && captain.wind.speedKnots > 0.5) {
    const wx = 50;
    const wy = 38;
    const wlen = 14 + captain.wind.speedKnots * 1.2;
    const wxe = wx + Math.cos(captain.wind.direction) * wlen;
    const wye = wy + Math.sin(captain.wind.direction) * wlen;
    ctx.strokeStyle = '#a8b8c8';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(wx, wy);
    ctx.lineTo(wxe, wye);
    ctx.stroke();
    const ang = captain.wind.direction;
    const head = 5;
    ctx.fillStyle = '#a8b8c8';
    ctx.beginPath();
    ctx.moveTo(wxe, wye);
    ctx.lineTo(wxe - Math.cos(ang - 0.4) * head, wye - Math.sin(ang - 0.4) * head);
    ctx.lineTo(wxe - Math.cos(ang + 0.4) * head, wye - Math.sin(ang + 0.4) * head);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = '#a8b8c8';
    ctx.font = '11px Georgia, serif';
    ctx.textAlign = 'left';
    ctx.fillText('Wind', 14, 18);
    ctx.textAlign = 'right';
    ctx.fillText(`${captain.wind.speedKnots.toFixed(0)} kt`, w - 14, 18);
  }

  // Position label (bottom-left)
  if (captain.ship) {
    const px = captain.ship.position.x;
    const py = captain.ship.position.y;
    ctx.fillStyle = 'rgba(247, 238, 216, 0.55)';
    ctx.font = '11px Georgia, serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    ctx.fillText(`Position: ${Math.abs(px).toFixed(0)} km ${px < 0 ? 'W' : 'E'}, ${Math.abs(py).toFixed(0)} km ${py < 0 ? 'N' : 'S'}`, 14, h - 14);
  }
  // Range / zoom label (bottom-right)
  ctx.fillStyle = 'rgba(247, 238, 216, 0.55)';
  ctx.font = '11px Georgia, serif';
  ctx.textAlign = 'right';
  ctx.fillText(`Sight: ${visualRange} km${hasTelescope ? ' (telescope)' : ''} · view: ${Math.round(radiusKm)} km`, w - 14, h - 14);
}

/** Hurricane spiral with a dashed radius ring. True hurricanes are red-tinted. */
function drawHurricane(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, isTrue: boolean): void {
  const ringStroke = isTrue ? 'rgba(196, 60, 32, 0.85)' : 'rgba(160, 160, 180, 0.7)';
  const fillTint = isTrue ? 'rgba(196, 60, 32, 0.10)' : 'rgba(160, 160, 180, 0.07)';
  // Soft tinted disk
  ctx.fillStyle = fillTint;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, 2 * Math.PI);
  ctx.fill();
  // Spiral arms — two arms of a logarithmic spiral
  ctx.strokeStyle = ringStroke;
  ctx.lineWidth = 1.6;
  for (const armOffset of [0, Math.PI]) {
    ctx.beginPath();
    let first = true;
    for (let t = 0.05; t <= 1.0; t += 0.05) {
      const radius = r * t;
      const angle = armOffset + t * 2.4 * Math.PI;
      const px = x + Math.cos(angle) * radius;
      const py = y + Math.sin(angle) * radius;
      if (first) { ctx.moveTo(px, py); first = false; } else ctx.lineTo(px, py);
    }
    ctx.stroke();
  }
  // Dashed extent ring
  ctx.setLineDash([6, 5]);
  ctx.strokeStyle = ringStroke;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, 2 * Math.PI);
  ctx.stroke();
  ctx.setLineDash([]);
  // Eye
  ctx.fillStyle = isTrue ? '#c43c20' : '#b8b8c8';
  ctx.beginPath();
  ctx.arc(x, y, Math.max(2, r * 0.04), 0, 2 * Math.PI);
  ctx.fill();
  // Label
  if (r > 18) {
    ctx.fillStyle = isTrue ? '#f7c5b8' : '#dcdce4';
    ctx.font = 'bold 10px Georgia, serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(isTrue ? 'HURRICANE' : 'TROPICAL STORM', x, y - r - 8);
  }
}

/** A small parchment castle/tower glyph for the captain's city of origin. */
function drawCityMarker(ctx: CanvasRenderingContext2D, x: number, y: number, name: string): void {
  ctx.save();
  ctx.translate(x, y);
  // Tower body
  ctx.fillStyle = '#d9a72a';
  ctx.strokeStyle = '#3a2a18';
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.rect(-6, -4, 12, 9);
  ctx.fill();
  ctx.stroke();
  // Crenellations
  ctx.fillStyle = '#3a2a18';
  for (const dx of [-6, -2, 2]) ctx.fillRect(dx, -7, 2, 3);
  // Door
  ctx.fillStyle = '#3a2a18';
  ctx.fillRect(-1.5, 1, 3, 4);
  // Pennant
  ctx.strokeStyle = '#3a2a18';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(0, -7); ctx.lineTo(0, -12);
  ctx.stroke();
  ctx.fillStyle = '#c43c20';
  ctx.beginPath();
  ctx.moveTo(0, -12); ctx.lineTo(5, -10.5); ctx.lineTo(0, -9);
  ctx.closePath();
  ctx.fill();
  ctx.restore();

  ctx.fillStyle = '#f7eed8';
  ctx.font = 'bold 11px Georgia, serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  ctx.fillText(name, x, y + 8);
}

/** A smaller cream/red town glyph for coastal trading towns (visible everywhere there's a coast). */
function drawCoastalTownMarker(ctx: CanvasRenderingContext2D, x: number, y: number, name: string): void {
  ctx.save();
  ctx.translate(x, y);
  // Town body — cream tower
  ctx.fillStyle = '#efe4cc';
  ctx.strokeStyle = '#3a2a18';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.rect(-4, -3, 8, 7);
  ctx.fill();
  ctx.stroke();
  // Roof / pennant
  ctx.fillStyle = '#c43c20';
  ctx.beginPath();
  ctx.moveTo(-4, -3); ctx.lineTo(0, -7); ctx.lineTo(4, -3);
  ctx.closePath();
  ctx.fill();
  ctx.restore();

  ctx.fillStyle = '#f7eed8';
  ctx.font = '10px Georgia, serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  ctx.fillText(name, x, y + 6);
}

/**
 * A small skull/cross headstone glyph for a lost captain. Persistent — every
 * memorial accumulates over the lifetime of the world and the chart shows them
 * wherever the player's fog has touched the cell.
 */
function drawMemorialMarker(ctx: CanvasRenderingContext2D, x: number, y: number, m: WreckMemorial, radiusKm: number): void {
  ctx.save();
  ctx.translate(x, y);
  // Headstone body (rounded top)
  ctx.fillStyle = '#3a2a18';
  ctx.strokeStyle = '#1a1208';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(-3.5, 4);
  ctx.lineTo(-3.5, -1);
  ctx.arc(0, -1, 3.5, Math.PI, 0, false);
  ctx.lineTo(3.5, 4);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  // Cross on the stone
  ctx.strokeStyle = '#d6c69a';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(0, -2.5); ctx.lineTo(0, 2);
  ctx.moveTo(-2, -0.5); ctx.lineTo(2, -0.5);
  ctx.stroke();
  ctx.restore();

  // Only show captain name when zoomed in enough that labels don't crowd
  if (radiusKm <= 600) {
    ctx.fillStyle = 'rgba(247, 238, 216, 0.7)';
    ctx.font = 'italic 9px Georgia, serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText(m.captainName, x, y + 6);
  }
}

/** A small anchor glyph for the captain's embark / cast-off point. */
function drawEmbarkMarker(ctx: CanvasRenderingContext2D, x: number, y: number): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.strokeStyle = '#f7eed8';
  ctx.fillStyle = '#f7eed8';
  ctx.lineWidth = 1.6;
  // Stock (top crossbar)
  ctx.beginPath();
  ctx.moveTo(-5, -5); ctx.lineTo(5, -5);
  ctx.stroke();
  // Shank
  ctx.beginPath();
  ctx.moveTo(0, -5); ctx.lineTo(0, 5);
  ctx.stroke();
  // Ring
  ctx.beginPath();
  ctx.arc(0, -7, 2, 0, 2 * Math.PI);
  ctx.stroke();
  // Arms (curved)
  ctx.beginPath();
  ctx.arc(0, 5, 5, Math.PI * 0.15, Math.PI * 0.85);
  ctx.stroke();
  // Flukes
  ctx.beginPath();
  ctx.moveTo(-4.7, 6); ctx.lineTo(-6.5, 8.5);
  ctx.moveTo(4.7, 6); ctx.lineTo(6.5, 8.5);
  ctx.stroke();
  ctx.restore();

  ctx.fillStyle = 'rgba(247, 238, 216, 0.75)';
  ctx.font = 'italic 10px Georgia, serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  ctx.fillText('cast off', x, y + 12);
}

/** A charted island (dim green disc) or wreck site (broken hull) from the captain's memory. */
function drawDiscovery(ctx: CanvasRenderingContext2D, x: number, y: number, d: ChartDiscovery, radiusKm: number): void {
  if (d.kind === 'island') {
    const onLand = LANDMASSES.some(m => pointInPolygon({ x: d.x, y: d.y }, m.polygon));
    if (onLand) {
      drawCoastalTownMarker(ctx, x, y, d.name);
      return;
    }
    const size = d.size ?? 2;
    const r = 4 + size * 2.5;
    ctx.fillStyle = 'rgba(61, 102, 68, 0.55)';
    ctx.strokeStyle = 'rgba(31, 58, 37, 0.8)';
    ctx.lineWidth = 1.2;
    ctx.setLineDash([2, 2]);
    ctx.beginPath();
    ctx.arc(x, y, r, 0, 2 * Math.PI);
    ctx.fill();
    ctx.stroke();
    ctx.setLineDash([]);
    if (radiusKm <= 1200) {
      ctx.fillStyle = 'rgba(247, 238, 216, 0.8)';
      ctx.font = '10px Georgia, serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillText(d.name, x, y + r + 3);
    }
    return;
  }
  // Wreck site — a broken hull: a tilted hull arc with a snapped mast
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(-0.25);
  ctx.strokeStyle = '#8b6c3a';
  ctx.fillStyle = '#5a452a';
  ctx.lineWidth = 1.4;
  ctx.beginPath();
  ctx.moveTo(-7, -1);
  ctx.quadraticCurveTo(0, 6, 7, -1);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(0, -1); ctx.lineTo(1.5, -8);
  ctx.moveTo(1.5, -8); ctx.lineTo(5, -5);
  ctx.stroke();
  ctx.restore();
  if (radiusKm <= 600) {
    ctx.fillStyle = 'rgba(247, 238, 216, 0.7)';
    ctx.font = 'italic 9px Georgia, serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText(d.name, x, y + 7);
  }
}

function agoLabel(ms: number): string {
  const min = Math.floor(ms / 60000);
  if (min < 60) return `${Math.max(1, min)} min ago`;
  const hrs = Math.floor(min / 60);
  if (hrs < 48) return `${hrs} h ago`;
  return `${Math.floor(hrs / 24)} d ago`;
}

/** Hollow, dashed ghost of the thing, pinned where it was last seen. */
function drawLastSeen(ctx: CanvasRenderingContext2D, x: number, y: number, c: ChartContact, radiusKm: number, now: number): void {
  ctx.save();
  ctx.setLineDash([2, 2]);
  ctx.lineWidth = 1.2;
  if (c.kind === 'whale') {
    ctx.strokeStyle = 'rgba(122, 141, 150, 0.9)';
    ctx.beginPath();
    ctx.ellipse(x, y, 6, 3, 0, 0, 2 * Math.PI);
    ctx.stroke();
  } else if (c.kind === 'fish_school') {
    ctx.strokeStyle = 'rgba(180, 200, 220, 0.7)';
    ctx.beginPath();
    ctx.arc(x, y, 4, 0, 2 * Math.PI);
    ctx.stroke();
  } else {
    ctx.strokeStyle = c.kind === 'captain' ? 'rgba(90, 138, 90, 0.95)'
      : c.kind === 'pirate_ship' ? 'rgba(30, 30, 30, 0.95)'
      : 'rgba(139, 108, 58, 0.95)';
    ctx.beginPath();
    ctx.moveTo(x, y - 6);
    ctx.lineTo(x + 5, y + 5);
    ctx.lineTo(x - 5, y + 5);
    ctx.closePath();
    ctx.stroke();
  }
  ctx.restore();
  if (radiusKm <= 600) {
    ctx.fillStyle = 'rgba(247, 238, 216, 0.65)';
    ctx.font = 'italic 9px Georgia, serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText(`${c.label} · ${agoLabel(Math.max(0, now - c.seenAt))}`, x, y + 8);
  }
}

function drawSighting(ctx: CanvasRenderingContext2D, x: number, y: number, s: Sighting, world: Vec2, pxPerKm: number): void {
  if (s.entityKind === 'island') {
    // Dotted landing-radius ring — drop anchor inside this circle and you'll land
    // at the island shop. Drawn first so the marker sits on top.
    const landingRpx = ISLAND_LANDING_RANGE_KM * pxPerKm;
    if (landingRpx >= 4) {
      ctx.save();
      ctx.strokeStyle = 'rgba(247, 238, 216, 0.45)';
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.arc(x, y, landingRpx, 0, 2 * Math.PI);
      ctx.stroke();
      ctx.restore();
    }
    // Coastal towns — islands sitting inside a landmass polygon — render as a small castle
    // (same glyph family as the player's origin city, scaled down a touch).
    const onLand = LANDMASSES.some(m => pointInPolygon(world, m.polygon));
    if (onLand) {
      drawCoastalTownMarker(ctx, x, y, s.islandName ?? 'town');
      return;
    }
    const size = s.islandSize ?? 2;
    const r = 4 + size * 2.5;
    ctx.fillStyle = '#3d6644';
    ctx.strokeStyle = '#1f3a25';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, 2 * Math.PI);
    ctx.fill();
    ctx.stroke();
    if (s.islandName) {
      ctx.fillStyle = '#f7eed8';
      ctx.font = '10px Georgia, serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillText(s.islandName, x, y + r + 3);
    }
    return;
  }
  if (s.entityKind === 'whale') {
    ctx.fillStyle = '#7a8d96';
    ctx.strokeStyle = '#3a2a18';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.ellipse(x, y, 6, 3, 0, 0, 2 * Math.PI);
    ctx.fill();
    ctx.stroke();
    return;
  }
  if (s.entityKind === 'fish_school') {
    ctx.fillStyle = 'rgba(180, 200, 220, 0.55)';
    for (let i = 0; i < 5; i++) {
      const a = i * (Math.PI * 2 / 5);
      ctx.beginPath();
      ctx.arc(x + Math.cos(a) * 3, y + Math.sin(a) * 3, 1.4, 0, 2 * Math.PI);
      ctx.fill();
    }
    return;
  }
  // ai_ship, pirate_ship, or captain — triangle
  const isCaptain = s.captainToken !== null;
  const isWar = s.flag === 'war';
  const isPirate = s.entityKind === 'pirate_ship';
  ctx.fillStyle = isPirate ? '#1c1c1c' : isCaptain ? (isWar ? '#c43c20' : '#5a8a5a') : '#8b6c3a';
  ctx.strokeStyle = '#3a2a18';
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.moveTo(x, y - 6);
  ctx.lineTo(x + 5, y + 5);
  ctx.lineTo(x - 5, y + 5);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  if (isCaptain && s.captainUsername) {
    ctx.fillStyle = '#f7eed8';
    ctx.font = '10px Georgia, serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText(s.captainUsername, x, y + 8);
  }
}
